import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { CAPS } from '@/lib/kbCaps';
import type {
  Language,
  TenantProfile,
  TenantProfileUpdateRequest,
} from '@/services/tenantProfileService';

const LANGUAGES = ['pt-BR', 'pt-PT'] as const satisfies readonly Language[];

export const tenantProfileSchema = z.object({
  business_name: z
    .string()
    .min(1, { message: 'onboarding.errors.business_name_required' })
    .max(CAPS.business_name, { message: 'onboarding.errors.business_name_too_long' }),
  owner_name: z
    .string()
    .min(1, { message: 'onboarding.errors.owner_name_required' })
    .max(CAPS.owner_name, { message: 'onboarding.errors.owner_name_too_long' }),
  language: z.enum(LANGUAGES, { message: 'onboarding.errors.language_invalid' }),
});

export type TenantProfileFormValues = z.infer<typeof tenantProfileSchema>;

export interface UseTenantProfileFormOptions {
  initial: Partial<TenantProfile> & { owner_name_fallback?: string };
}

/** Step 1 form hook — business_name, owner_name, language. */
export function useTenantProfileForm({ initial }: UseTenantProfileFormOptions) {
  return useForm<TenantProfileFormValues>({
    resolver: zodResolver(tenantProfileSchema),
    defaultValues: {
      business_name: initial.business_name ?? '',
      owner_name: initial.owner_name ?? initial.owner_name_fallback ?? '',
      language: (initial.language ?? 'pt-BR') as Language,
    },
  });
}

/** Build the PATCH body: only fields whose value diverges from ``initial``. */
export function diffForUpdate(
  values: TenantProfileFormValues,
  initial: Partial<TenantProfile>
): TenantProfileUpdateRequest {
  const out: TenantProfileUpdateRequest = {};
  if (values.business_name !== (initial.business_name ?? '')) {
    out.business_name = values.business_name;
  }
  if (values.owner_name !== (initial.owner_name ?? '')) {
    out.owner_name = values.owner_name;
  }
  if (values.language !== initial.language) {
    out.language = values.language;
  }
  return out;
}
